import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  needToSaveSettings,
  savedConfiguration,
  selectedCadTypes,
  selectedDocumentTypes,
  selectedLanguages,
  selectedProducts,
  sessionLanguage,
  storedSession,
} from '../../../redux/mydoc.selector';
import { combineLatest, first } from 'rxjs';
import { IExportConfiguration } from '../../../models/export/export-configuration.model';
import { ISession } from '../../../models/session/session.models';
import {
  getValue,
  isDefined,
  isDefinedAndFilledArray,
  SOVerify,
} from '../../utility/verify.lib';
import {
  postSaveExportConfiguration,
  postSaveUserEntries,
} from '../../../redux/mydoc.action';
import { ILanguageText } from '../../../models/language/language-text.model';
import { IProductItem } from '../../../models/item/product-item.model';
import { IDocumentTypeText } from '../../../models/document/document-type-text.model';
import { ICadType } from '../../../models/document/cad-type.model';
import { IDocumentSimpleType } from '../../../models/document/document-simple-type.model';
import { IExportSimpleStructure } from '../../../models/export/export-simple-structure.model';
import { IExportStructure } from '../../../models/export/export-structure.model';

@Injectable({
  providedIn: 'root',
})
export class SavingExportService {
  public needToSaveSettings$ = this.store.select(needToSaveSettings);
  public savedConfiguration$ = this.store.select(savedConfiguration);
  public storedSession$ = this.store.select(storedSession);
  private selectedLanguages$ = this.store.select(selectedLanguages);
  private selectedProducts$ = this.store.select(selectedProducts);
  private selectedDocumentTypes$ = this.store.select(selectedDocumentTypes);
  private selectedCadTypes$ = this.store.select(selectedCadTypes);

  public constructor(private store: Store) {}

  public saveUserSettings(): void {
    combineLatest([
      this.needToSaveSettings$,
      this.savedConfiguration$,
      this.storedSession$,
    ])
      .pipe(first())
      .subscribe(
        ([saveSettings, config, session]: [
          boolean,
          IExportConfiguration,
          ISession
        ]) => {
          if (isDefined(() => session.id)) {
            const changedConfig: any = {
              ...config,
              folderSetting: this.getSimpleSettings(
                getValue(() => config.folderSetting)
              ),
              fileSetting: this.getSimpleSettings(
                getValue(() => config.fileSetting)
              ),
            };

            this.store.dispatch(
              postSaveExportConfiguration({
                sessionId: session.id,
                orderNo: session.orderNumber,
                saveAsDefault: saveSettings,
                configuration: changedConfig,
              })
            );
          }
        }
      );
  }

  public saveUserEntries(): void {
    combineLatest([
      this.storedSession$,
      this.selectedLanguages$,
      this.selectedProducts$,
      this.selectedDocumentTypes$,
      this.selectedCadTypes$,
    ])
      .pipe(first())
      .subscribe(
        ([session, languages, products, documentTypes, subTypes]: [
          ISession,
          ILanguageText[],
          IProductItem[],
          IDocumentTypeText[],
          ICadType[]
        ]) => {
          if (
            isDefined(() => session.id) &&
            isDefined(() => session.orderNumber)
          ) {
            const languagesString: string = this.getTextList(languages, false);
            const productsString: string = this.getTextList(products, true);
            const documentTypesSimpleList: IDocumentSimpleType[] =
              this.getSimpleDocumentTypes(documentTypes);
            const subTypesSimpleList: IDocumentSimpleType[] =
              this.getSimpleDocumentTypes(subTypes);
            this.store.dispatch(
              postSaveUserEntries({
                sessionId: session.id,
                orderNo: session.orderNumber,
                docLanguages: languagesString,
                selectedProducts: productsString,
                documentTypes: documentTypesSimpleList,
                documentSubTypes: subTypesSimpleList,
              })
            );
          }
        }
      );
  }

  private getSimpleSettings(
    settings: IExportStructure[]
  ): IExportSimpleStructure[] {
    const returnValue: IExportSimpleStructure[] = [];

    if (isDefinedAndFilledArray(() => settings)) {
      settings.forEach((setting: IExportStructure) => {
        if (
          isDefined(() => setting.level) &&
          isDefined(() => setting.shortName)
        ) {
          returnValue.push({
            level: setting.level,
            shortName: setting.shortName,
          });
        }
      });
    }

    return returnValue;
  }

  private getTextList(
    entries: IProductItem[] | ILanguageText[],
    isProductItem: boolean
  ): string {
    let returnValue: string = '';

    if (isDefinedAndFilledArray(() => entries)) {
      const textList: string[] = entries.map((entry) =>
        isProductItem
          ? getValue(() => (<IProductItem>entry).posNumber, '')
          : getValue(() => (<ILanguageText>entry).languageId, '')
      );
      returnValue = textList.join(',');
    }

    return returnValue;
  }

  private getSimpleDocumentTypes(
    types: IDocumentTypeText[] | ICadType[]
  ): IDocumentSimpleType[] {
    let returnValue: IDocumentSimpleType[] = [];

    if (isDefinedAndFilledArray(() => types)) {
      types.forEach((type: IDocumentTypeText | ICadType) => {
        const simpleType: IDocumentSimpleType = {
          docTypeId: getValue(() => type.docTypeId, null),
        };
        const subTypeId: number = SOVerify.getValueFromStructure(
          type,
          'subTypeId'
        );

        if (isDefined(() => subTypeId)) {
          simpleType.subTypeId = subTypeId;
        }

        returnValue.push(simpleType);
      });
    }

    return returnValue;
  }
}
